import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tooltip,
  Typography,
  Badge,
  Upload as UploadAntd,
  Avatar,
  Image,
} from "antd";
import {
  EyeOutlined,
  PlusOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import lang from "../helper/lang";
import { Eye, UploadCloud } from "lucide-react";
import apiPath from "../constants/apiPath";
import TextArea from "antd/lib/input/TextArea";
import moment from "moment";
import SingleImageUpload from "../components/SingleImageUpload";
import notfound from "../assets/images/not_found.png";
import PhoneInput from "react-phone-input-2";
import { TreeSelect } from "antd";

import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getExampleNumber } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";

const { Search } = Input;
const { Title, Text, Paragraph } = Typography;

function ReportManager() {
  const { request } = useRequest();

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState();
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [reports, setReports] = useState([]);
  const [modalType, setModalType] = useState("add");
  const [form2] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [editMetaVisible, setEditMetaVisible] = useState(false);
  const [editMetaLoading, setEditMetaLoading] = useState(false);
  const [editMetaForm] = Form.useForm();

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const FileType = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/webp",
    "application/pdf",
  ];

  console.log("countryCode++-->", countryCode);

  // Loading states for different operations
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [categories, setCategories] = useState([]);
  const [labs, setLabs] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const debouncedSearchText = useDebounce(searchText, 500);
  const isInitialRender = useRef(true);
  const isLabReport = !!selectedUser?.lab;
  const [currentStep, setCurrentStep] = useState(1);
  const [formStep1] = Form.useForm();
  const [formStep2] = Form.useForm(); // step 2: user stats
  const [formStep3] = Form.useForm(); // step 3: doctor + dates + remarks
  const [reportFile, setReportFile] = useState(null);
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [phoneCountryIso, setPhoneCountryIso] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const cardStyle = {
    background: "#1e1e2f",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "12px",
    height: "100%",
  };

  const MetaRow = ({ label, value }) => (
    <div style={{ marginBottom: 8 }}>
      <Text strong style={{ color: "#bbb" }}>
        {label}:{" "}
      </Text>
      <Text style={{ color: "#eee" }}>{value || "N/A"}</Text>
    </div>
  );

  // Helper function to map API status to boolean
  const mapStatusToBoolean = (status) => {
    return status === "Active" || status === "active";
  };

  // Fetch users
  const fetchUsers = useCallback(() => {
    setLoading(true);

    request({
      url: `/api/admin/users`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let usersData = [];
        if (data?.data) {
          if (Array.isArray(data.data)) {
            usersData = data.data;
          }
        } else if (Array.isArray(data)) {
          usersData = data;
        }

        setUsers(Array.isArray(usersData) ? usersData : []);
      },
      onError: (error) => {
        setLoading(false);
        setUsers([]);
        console.log("Error occurred while fetch users", error);
      },
    });
  }, [debouncedSearchText, pagination.current, pagination.pageSize, request]);

  const fetchReports = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearchText) params.append("search", debouncedSearchText);
    if (selectedCategory)
      params.append("category_id", selectedCategory);
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    request({
      url: `/api/admin/reports?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let reportsData = [];
        let totalCount = 0;
        let currentPageFromApi = undefined;
        let limitFromApi = undefined;

        if (data?.data) {
          // Common shapes: { data: { users, pagination } } or { data: { docs, totalDocs } }
          if (Array.isArray(data.data)) {
            reportsData = data.data;
          }

          const meta = data.data.pagination || data.pagination || {};
          totalCount =
            meta.totalCount ?? meta.total ?? data.data.totalDocs ?? 0;
          currentPageFromApi = meta.currentPage ?? meta.page ?? meta.pageNumber;
          limitFromApi = meta.limit ?? meta.pageSize;
        } else if (Array.isArray(data)) {
          reportsData = data;
          totalCount = data.length;
        }

        setReports(Array.isArray(reportsData) ? reportsData : []);
        setPagination((prev) => ({
          ...prev,
          total: totalCount,
          current: currentPageFromApi ?? prev.current,
          pageSize: limitFromApi ?? prev.pageSize,
        }));
      },
      onError: (error) => {
        setLoading(false);
        setReports([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast(
          `${
            error.message ||
            error.response.data.message ||
            error.response.message ||
            "Failed to terminate session"
          }`,
          Severty.ERROR,
        );
      },
    });
  }, [debouncedSearchText, selectedCategory, pagination.current, pagination.pageSize, request]);

  const fetchLabs = useCallback(() => {
    setLoading(true);
    request({
      url: `/api/admin/labs`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let labsData = [];
        if (data?.data) {
          if (Array.isArray(data.data)) {
            labsData = data.data;
          }
        }
        setLabs(Array.isArray(labsData) ? labsData : []);
      },
      onError: (error) => {
        setLoading(false);
        setLabs([]);
        ShowToast("Failed to fetch labs", Severty.ERROR);
      },
    });
  }, []);

  const fetchDoctors = useCallback(() => {
    request({
      url: `/api/admin/${apiPath.doctors}`,
      method: "GET",
      onSuccess: (data) => {
        let doctorsData = [];
        if (data?.data) {
          doctorsData = Array.isArray(data.data) ? data.data : [];
        }
        setDoctors(doctorsData);
      },
      onError: () => {
        setDoctors([]);
      },
    });
  }, [request]);

  const fetchCategories = useCallback(() => {
    setLoading(true);
    request({
      url: `/api/admin/document-categories?status=ACTIVE`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setCategories(data.data);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast("Failed to fetch document categories", Severty.ERROR);
      },
    });
  }, []);

  const buildCategoryTree = (categories = []) => {
    return categories.map((cat) => ({
      title: cat.name,
      value: cat.id,
      key: cat.id,
      children: Array.isArray(cat.children)
        ? buildCategoryTree(cat.children)
        : [],
    }));
  };

  // const handleImage = (data) => {
  //   if (data && data.length > 0) {
  //     setImage(data[0].url);

  //     // ✅ VERY IMPORTANT
  //     form.setFieldsValue({
  //       image: data[0].url,
  //     });
  //   } else {
  //     setImage("");
  //     form.setFieldsValue({ image: null });
  //   }
  // };

  const handleImage = (data) => {
    console.log("data==>", data);
    if (!data || data.length === 0) {
      setImage(null);
      return;
    }

    const file = data[0];

    // ✅ New file
    if (file.originFileObj) {
      setImage(file.originFileObj);
      return;
    }

    // ✅ Existing uploaded file (URL)
    if (file.url || file.thumbUrl) {
      setImage(file.url || file.thumbUrl);
    }
  };

  const getExpectedLocalPhoneLength = (country) => {
    if (!country?.format || !country?.dialCode) return null;

    const totalDigits = (country.format.match(/\./g) || []).length;
    const countryCodeLength = country.dialCode.length;

    const localDigits = totalDigits - countryCodeLength;
    return localDigits > 0 ? localDigits : null;
  };

  useEffect(() => {
    fetchUsers();
    fetchCategories();
    fetchReports();
    fetchLabs();
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    fetchReports();
  }, [debouncedSearchText, selectedCategory, pagination.current, pagination.pageSize]);

  // Handlers
  const handleTableChange = (paginationInfo, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddUser = () => {
    setModalType("add");
    setCurrentStep(1);
    formStep1.resetFields();
    formStep2.resetFields();
    formStep3.resetFields();

    // 🔥 reset phone validation state
    setPhoneCountry(null);
    setCountryCode("");

    setModalVisible(true);
  };

  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setModalType("view");
    setModalVisible(true);
  };

  //   ------------ USER Create and Update ----------
  const handleFormSubmit = async (values) => {
    console.log("FORM VALUES:", values);
    try {
      const formData = new FormData();

      // ---------- BASIC REPORT INFO ----------
      formData.append("category_id", values.category_id || "");
      formData.append("customer_id", values.customer_id || "");
      formData.append("customer_name", values.customer_name || "");
      formData.append("customer_email", values.email || "");
      formData.append(
        "customer_mobile",
        values.mobile ? values.mobile.slice(countryCode.length) : "",
      );
      formData.append("country_code", countryCode || "");
      formData.append("lab_id", values.lab_id || "");
      formData.append("notes", values.notes || "");

      // ---------- MEDICAL INFO (doctor name/qualification from selected doctor) ----------
      const selectedDoctor = values.doctor_id
        ? doctors.find(
            (d) => d.id === values.doctor_id || d.id === Number(values.doctor_id),
          )
        : null;
      formData.append("doctor_id", values.doctor_id || "");
      formData.append(
        "doctor_name",
        selectedDoctor?.name || values.doctor_name || "",
      );
      formData.append(
        "doctor_qualification",
        selectedDoctor?.specialization || values.doctor_qualification || "",
      );
      formData.append("test_date", values.test_date || "");
      formData.append("report_date", values.report_date || "");
      formData.append("last_visit", values.last_visit || "");
      formData.append("next_visit", values.next_visit || "");
      formData.append("remarks", values.remarks || "");

      // ---------- USER STATS (step 2) ----------
      formData.append("temperature", values.temperature ?? "");
      formData.append("weight", values.weight ?? "");
      formData.append("height", values.height ?? "");
      formData.append("blood_pressure", values.blood_pressure ?? "");
      formData.append("blood_glucose", values.blood_glucose ?? "");
      formData.append("oxygen_saturation", values.oxygen_saturation ?? "");
      formData.append("pulse", values.pulse ?? "");

      // ---------- FILE ----------
      if (!(reportFile instanceof File)) {
        ShowToast("Please upload a valid report file", Severty.ERROR);
        return;
      }

      formData.append("report_file", reportFile);

      setCreateLoading(true);

      request({
        url: `/api/admin/reports`,
        method: "POST",
        data: formData,

        onSuccess: () => {
          ShowToast("Report created successfully", Severty.SUCCESS);
          setModalVisible(false);
          setCurrentStep(1);
          formStep2.resetFields();
          formStep3.resetFields();
          fetchReports();
          setCreateLoading(false);
          setPhoneCountryIso(null);
        },
        onError: (error) => {
          console.log("error", error);
          ShowToast(
            `${
              error.response.data.message ||
              error.message ||
              error.response.message ||
              "Failed to create Report"
            }`,
            Severty.ERROR,
          );
          setCreateLoading(false);
          setPhoneCountryIso(null);
        },
      });
    } catch (error) {
      ShowToast("Failed to save user", Severty.ERROR);
      setCreateLoading(false);
      setUpdateLoading(false);
      setPhoneCountryIso(null);
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      const userId = user._id || user.id;
      setDeleteLoading((prev) => ({ ...prev, [userId]: true }));
      request({
        url: `/api/admin/users/${userId}`,
        method: "DELETE",
        onSuccess: (data) => {
          ShowToast("User deleted successfully", Severty.SUCCESS);
          setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
          fetchUsers();
        },
        onError: () => {
          ShowToast("Failed to delete user", Severty.ERROR);
          setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
        },
      });
    } catch (error) {
      ShowToast("Failed to delete user", Severty.ERROR);
      const userId = user._id || user.id;
      setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleUpdateMeta = (values) => {
    if (!selectedUser?.id) return;

    setEditMetaLoading(true);

    request({
      url: `/api/admin/reports/${selectedUser.id}`,
      method: "PUT",
      data: {
        last_visit: values.last_visit || null,
        next_visit: values.next_visit || null,
        remarks: values.remarks || "",
      },
      onSuccess: () => {
        ShowToast("Treatment info updated", Severty.SUCCESS);
        setEditMetaVisible(false);
        fetchReports(); // refresh list + view modal
        fetchSingleReport(selectedUser.id);
        setEditMetaLoading(false);
      },
      onError: (error) => {
        ShowToast(
          error?.response?.data?.message || "Failed to update info",
          Severty.ERROR,
        );
        setEditMetaLoading(false);
      },
    });
  };

  const fetchSingleReport = (reportId) => {
    request({
      url: `/api/admin/reports/${reportId}`,
      method: "GET",
      onSuccess: (res) => {
        // API may return { data } or object directly
        const reportData = res?.data || res;
        setSelectedUser(reportData);
      },
      onError: () => {
        ShowToast("Failed to refresh report data", Severty.ERROR);
      },
    });
  };

  const handleUpdateUserPassword = async (values) => {
    try {
      const payload = {
        user_id: selectedUser.id,
        new_password: values.password,
        confirm_new_password: values.confirm_password,
      };
      // Reset the password with the new password entered by the user
      request({
        url: "/auth/change-password",
        method: "POST",
        data: payload,
        onSuccess: (data) => {
          ShowToast(data.message, Severty.SUCCESS);
          setChangePasswordModal(false);
          form2.resetFields();
        },
        onError: (error) => {
          ShowToast(error.response.data.message, Severty.ERROR);
        },
      });
    } catch (error) {
      ShowToast(
        `${
          error.message ||
          error.response.data.message ||
          error.response.message ||
          "Failed to terminate session"
        }`,
        Severty.ERROR,
      );
      console.log("Error occured while update password", error);
    }
  };

  // const handleCustomerSelect = (customerId) => {
  //   const selectedCustomer = users.find((user) => user.id === customerId);

  //   if (!selectedCustomer) return;

  //   form.setFieldsValue({
  //     customer_name: selectedCustomer.full_name,
  //     email: selectedCustomer.email,
  //     mobile: `${selectedCustomer.country_code}${selectedCustomer.mobile}`,
  //   });
  // };

  const isValidPhoneNumber = (phone, countryCode) => {
    if (!phone || !countryCode) return false;

    const phoneNumber = parsePhoneNumberFromString(
      phone,
      countryCode.toUpperCase(),
    );

    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const getPhoneExample = (countryCode) => {
    console.log("country_code==>", countryCode);
    try {
      const example = getExampleNumber(countryCode, examples);
      if (!example) return null;

      const intl = example.formatInternational();
      // "+91 62654 53127"

      // keep + and spaces, mask digits except country code
      const countryCallingCode = example.countryCallingCode;

      let seenCountryCode = false;

      return intl.replace(/\d+/g, (match) => {
        // first numeric chunk is country calling code
        if (!seenCountryCode) {
          seenCountryCode = true;
          return match; // keep country code
        }
        return "x".repeat(match.length);
      });
    } catch {
      return null;
    }
  };

  const handleCustomerSelect = (customerId) => {
    const selectedCustomer = users.find((user) => user.id === customerId);

    if (!selectedCustomer) return;

    setCountryCode(selectedCustomer.country_code); // ✅ IMPORTANT

    formStep1.setFieldsValue({
      customer_name: selectedCustomer.full_name,
      email: selectedCustomer.email,
      mobile: `${selectedCustomer.country_code}${selectedCustomer.mobile}`,
    });
  };

  const handleDoctorSelect = (doctorId) => {
    if (!doctorId) {
      formStep3.setFieldsValue({ doctor_qualification: undefined });
      return;
    }
    const doctor = doctors.find(
      (d) => d.id === doctorId || d.id === Number(doctorId),
    );
    if (doctor) {
      formStep3.setFieldsValue({
        doctor_qualification: doctor.specialization || "",
      });
    }
  };

  const handleDownloadReport = (fileUrl) => {
    if (!fileUrl) return;

    const link = document.createElement("a");
    link.href = fileUrl;

    // Extract file name from URL
    link.download = fileUrl.split("/").pop();

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "Report ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id) => <Text code>REPORT_{id}</Text>,
    },
    {
      title: "Report Category",
      // dataIndex: ["category", "name"],
      key: "report_category",
      width: 120,
      render: (_, record) => (
        <Text code>{record?.category?.name || "N/A"}</Text>
      ),
    },
    {
      title: "Patient Name",
      // dataIndex: ["customer", "full_name"],
      key: "patient_name",
      width: 250,
      sorter: (a, b) => {
        const nameA =
          a.meta?.patient?.full_name || a.customers?.[0]?.full_name || "";
        const nameB =
          b.meta?.patient?.full_name || b.customers?.[0]?.full_name || "";
        return nameA.localeCompare(nameB);
      },
      render: (_, record) => {
        // ✅ PRIORITY: META PATIENT
        if (record?.meta?.patient) {
          const p = record.meta.patient;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Avatar icon={<UserOutlined />} src={p.profile_img} />
              <span>{p.full_name}</span>
            </div>
          );
        }

        // ⛔ FALLBACK: OLD MULTI-CUSTOMER LOGIC
        const customers = record.customers || [];
        if (customers.length === 0) {
          return <Text type="secondary">N/A</Text>;
        }

        if (customers.length === 1) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Avatar icon={<UserOutlined />} src={customers[0].profile_img} />
              <span>{customers[0].full_name}</span>
            </div>
          );
        }

        const firstCustomer = customers[0];
        const remainingCount = customers.length - 1;

        return (
          <Tooltip title={customers.map((c) => c.full_name).join(", ")}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Avatar icon={<UserOutlined />} src={firstCustomer.profile_img} />
              <span>
                {firstCustomer.full_name}
                <Text type="secondary"> +{remainingCount} more</Text>
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Phone Number",
      // dataIndex: ["customer", "mobile"],
      key: "mobile",
      width: 220,
      render: (_, record) => {
        const customers = record.customers || [];

        if (customers.length === 0) return "N/A";

        const formatMobile = (c) =>
          `${c.country_code ? `${c.country_code} ` : ""}${c.mobile || ""}`;

        if (customers.length === 1) {
          return formatMobile(customers[0]);
        }

        return (
          <Tooltip
            title={customers
              .map((c) =>
                c?.full_name
                  ? `${c.full_name}: ${formatMobile(c)}`
                  : formatMobile(c),
              )
              .filter(Boolean)
              .join("  ")}
          >
            <Text>
              {formatMobile(customers[0])}
              <Text type="secondary"> +{customers.length - 1} more</Text>
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Uploaded By",
      dataIndex: ["uploaded_by", "full_name"],
      key: "uploaded_by",
      width: 200,
      render: (uploaded_by) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{uploaded_by || "N/A"}</span>
        </div>
      ),
    },
    {
      title: "Upload Date",
      dataIndex: "created_at",
      key: "uploaded_date",
      width: 120,
      render: (uploaded_date) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {uploaded_date
              ? moment(uploaded_date).format("YYYY MM DD, hh:mm A")
              : "N/A"}
          </div>
        );
      },
    },
    {
      title: lang("Actions") || "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={"View Report"}>
            <Button
              className="button-action-small"
              icon={<Eye />}
              onClick={() => handleViewProfile(record)}
              disabled={deleteLoading[record._id || record.id]}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const InfoRow = ({ label, value, badge }) => (
    <div style={{ marginBottom: 8 }}>
      <Text strong style={{ color: "#bbb" }}>
        {label}:{" "}
      </Text>

      {badge ? (
        <Badge status={badge === "Active" ? "success" : "error"} text={badge} />
      ) : (
        <Text style={{ color: "#eee" }}>{value || "N/A"}</Text>
      )}
    </div>
  );

  return (
    <div>
      <Card
        title={
          <>
            <TeamOutlined style={{ marginRight: 8 }} />
            {"Report Management"}
            <p>Admin manages reports : view, add as necessary.</p>
          </>
        }
        extra={
          <>
            <Search
              placeholder={
                lang("Search_by_name_or_email") || "Search by name or email"
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: "100%" }}
            />

            <TreeSelect
              allowClear
              placeholder="Filter by Category"
              style={{ width: 250 }}
              treeData={buildCategoryTree(categories)}
              treeDefaultExpandAll
              value={selectedCategory}
              onChange={(value) => {
                setSelectedCategory(value);
                setPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));
              }}
            />
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddUser}
            >
              {"Add Report"}
            </Button>
          </>
        }
      >
        {/* Users Table */}
        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={Array.isArray(reports) ? reports : []}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${lang("Showing") || "Showing"} ${range[0]}-${range[1]} ${
                  lang("of") || "of"
                } ${total} ${lang("reports") || "reports"}`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      {/* Add/Edit Report Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserOutlined style={{ color: "#1890ff" }} />
            Add Report
          </div>
        }
        open={modalVisible && modalType !== "view"}
        onCancel={() =>
          !createLoading && !updateLoading && setModalVisible(false) && setPhoneCountryIso(null)
        }
        footer={null}
        width={700}
        confirmLoading={createLoading || updateLoading}
      >
        {/* ================= STEP 1 ================= */}
        {currentStep === 1 && (
          <Form
            form={formStep1}
            layout="vertical"
            onFinish={(values) => handleFormSubmit(values)}
            onFinishFailed={(err) => {
              console.log("FORM VALIDATION FAILED:", err);
              ShowToast(
                err[0]?.errors[0] || "Please fill required fields",
                Severty.ERROR,
              );
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="category_id"
                  label="Category"
                  rules={[
                    { required: true, message: "Please select category" },
                  ]}
                >
                  <TreeSelect
                    placeholder="Select Category"
                    treeData={buildCategoryTree(categories)}
                    treeDefaultExpandAll
                    allowClear
                    showSearch
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="customer_id" label="Choose Customer Id">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    optionLabelProp="name"
                    placeholder="Search by name or email"
                    onChange={handleCustomerSelect}
                    filterOption={(input, option) => {
                      const searchText = input.toLowerCase();
                      const label = option?.label?.toLowerCase() || "";
                      return label.includes(searchText);
                    }}
                  >
                    {users.map((u) => (
                      <Select.Option
                        key={u.id}
                        value={u.id}
                        label={`${u.full_name} ${u.email || ""} ${u.country_code || ""}${u.mobile || ""}`}
                        name={u.full_name}
                      >
                        <strong>{u.full_name}</strong>
                        <div style={{ fontSize: 12 }}>{u.email}</div>
                        <div style={{ fontSize: 12 }}>
                          {u.country_code} {u.mobile}
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="customer_name"
                  label="Name"
                  rules={[
                    { required: true, message: "Customer name required" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ required: true, message: "Email required" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="mobile"
                  label="Mobile Number"
                  help={
                    phoneCountryIso
                      ? `Example: ${getPhoneExample(phoneCountryIso)}`
                      : null
                  }
                  rules={[
                    { required: true, message: "Mobile number required" },
                    () => ({
                      validator(_, value) {
                        if (!value || !phoneCountryIso) return Promise.resolve();
                        if (!phoneCountry) return Promise.resolve(); // 🛡️ prevents ghost error

                        const digitsOnly = value.replace(/\D/g, "");
                        const localNumber = digitsOnly.slice(
                          phoneCountry.dialCode.length,
                        );

                        if (!/^\d+$/.test(localNumber)) {
                          return Promise.reject(
                            new Error("Phone number must contain only digits"),
                          );
                        }

                        // const expectedLength =
                        //   getExpectedLocalPhoneLength(phoneCountry);

                        const valid = isValidPhoneNumber(value, phoneCountryIso);

                      return valid
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error("Invalid phone number for selected country")
                          );
                      },
                    }),
                  ]}
                >
                  <PhoneInput
                    country="in"
                    enableSearch
                    value={formStep1.getFieldValue("mobile")}
                    onChange={(value, country) => {
                      formStep1.setFieldsValue({ mobile: value });
                      setCountryCode(country.dialCode);
                      setPhoneCountry(country); // 🔥 critical
                      setPhoneCountryIso(country.countryCode.toUpperCase());
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "40px",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="lab_id"
                  label="Lab"
                  rules={[{ required: true, message: "Please select lab" }]}
                >
                  <Select showSearch optionFilterProp="label">
                    {labs.map((lab) => (
                      <Select.Option
                        key={lab.id}
                        value={lab.id}
                        label={`${lab.name} ${lab.location?.city || ""}`}
                      >
                        <strong>{lab.name}</strong>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24} md={12}>
                <Form.Item
                  name="report_file"
                  label="Upload Report"
                  rules={[
                    { required: true, message: "Please upload report file" },
                  ]}
                  valuePropName="fileList"
                  getValueFromEvent={(e) => e?.fileList}
                >
                  <UploadAntd
                    accept=".png,.jpg,.jpeg,.webp,.pdf"
                    maxCount={1}
                    beforeUpload={() => false} // ⛔ STOP AUTO UPLOAD
                    onChange={({ fileList }) => {
                      if (fileList.length > 0) {
                        setReportFile(fileList[0].originFileObj); // ✅ RAW FILE
                      } else {
                        setReportFile(null);
                      }
                    }}
                    listType="picture"
                  >
                    <Button icon={<UploadCloud />}>Upload Report</Button>
                  </UploadAntd>
                </Form.Item>
                <p className="img-size-details">
                  You can select only image, pdf format files upto 5 MB file
                  size
                </p>
                {
                  <div className="mt-2 add-img-product">
                    {" "}
                    {image instanceof File && (
                      <Image
                        width={120}
                        src={URL.createObjectURL(image)}
                        onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                      />
                    )}
                  </div>
                }
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="notes" label={"Notes"}>
                  <TextArea placeholder={"Enter Notes"} />
                </Form.Item>
              </Col>
            </Row>
            <div style={{ textAlign: "right" }}>
              <Button
                type="primary"
                onClick={() => {
                  formStep1
                    .validateFields()
                    .then(() => setCurrentStep(2))
                    .catch(() =>
                      ShowToast("Please complete step 1", Severty.ERROR),
                    );
                }}
              >
                Next
              </Button>
            </div>
          </Form>
        )}
        {/* ================= STEP 2: User stats ================= */}
        {currentStep === 2 && (
          <Form
            form={formStep2}
            layout="vertical"
            onFinish={() => {
              formStep2.validateFields().then(() => setCurrentStep(3));
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="temperature" label="Temperature">
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 98.6"
                    addonAfter="°F"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="weight" label="Weight">
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 70"
                    addonAfter="kg"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="height" label="Height">
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 170"
                    addonAfter="cm"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="blood_pressure" label="Blood Pressure">
                  <Input placeholder="e.g. 120/80" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="blood_glucose" label="Blood Glucose">
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 100"
                    addonAfter="mg/dL"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="oxygen_saturation" label="Oxygen Saturation">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="e.g. 98"
                    addonAfter="%"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="pulse" label="Pulse">
                  <Input
                    type="number"
                    min={0}
                    placeholder="e.g. 72"
                    addonAfter="bpm"
                  />
                </Form.Item>
              </Col>
            </Row>
            <div style={{ textAlign: "right" }}>
              <Button onClick={() => setCurrentStep(1)}>Back</Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{ marginLeft: 8 }}
              >
                Next
              </Button>
            </div>
          </Form>
        )}

        {/* ================= STEP 3: Doctor + dates + remarks ================= */}
        {currentStep === 3 && (
          <Form
            form={formStep3}
            layout="vertical"
            onFinish={(step3Values) => {
              const step1Values = formStep1.getFieldsValue(true);
              const step2Values = formStep2.getFieldsValue(true);
              handleFormSubmit({
                ...step1Values,
                ...step2Values,
                ...step3Values,
              });
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="doctor_id"
                  label="Doctor Name"
                  rules={[
                    {
                      required: true,
                      message: "Please select a doctor",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select doctor"
                    optionFilterProp="label"
                    onChange={handleDoctorSelect}
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={doctors.map((d) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="doctor_qualification"
                  label="Specialization"
                >
                  <Input placeholder="Auto-filled when you select a doctor" readOnly />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="test_date" label="Test Date">
                  <Input type="date" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="report_date" label="Report Date">
                  <Input type="date" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="last_visit" label="Last Visit">
                  <Input
                    type="date"
                    onChange={() => formStep3.validateFields(["next_visit"])}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="next_visit"
                  label="Next Visit"
                  tooltip="Leave empty if treatment is completed"
                  dependencies={["last_visit"]}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve(); // allow empty

                        const lastVisit = formStep3.getFieldValue("last_visit");

                        if (!lastVisit) return Promise.resolve();

                        if (moment(value).isSameOrBefore(moment(lastVisit))) {
                          return Promise.reject(
                            new Error(
                              "Next visit must be after last visit date",
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    type="date"
                    onChange={() => formStep3.validateFields(["last_visit"])}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="remarks" label="Remarks">
              <TextArea rows={3} />
            </Form.Item>
            <div style={{ textAlign: "right" }}>
              <Button onClick={() => setCurrentStep(2)}>Back</Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={createLoading}
                disabled={createLoading}
                style={{ marginLeft: 8 }}
              >
                {createLoading ? "Saving..." : "Save Report"}
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      {/* View Report Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <EyeOutlined style={{ color: "#1890ff" }} />
            {`${
              Array.isArray(selectedUser?.customers) &&
              selectedUser.customers.length
                ? selectedUser.customers
                    .map((c) => c?.full_name)
                    .filter(Boolean)
                    .join(", ")
                : "Patient"
            } Report`}
          </div>
        }
        open={modalType === "view" && modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            {lang("Close") || "Close"}
          </Button>,
          selectedUser?.meta && (
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                editMetaForm.setFieldsValue({
                  last_visit: selectedUser.meta.last_visit,
                  next_visit: selectedUser.meta.next_visit,
                  remarks: selectedUser.meta.remarks,
                });
                setEditMetaVisible(true);
              }}
            >
              Edit Medical Info
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedUser && (
          <div>
            {/* HEADER (COMMON FOR BOTH) */}
            <Card
              style={{
                marginBottom: 24,
                background: "linear-gradient(135deg, #2c3e50 0%, #4b6cb7 100%)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
              }}
              bodyStyle={{ padding: "24px" }}
            >
              <Row gutter={[16, 16]} align="middle">
                <Col span={16}>
                  <Title level={2} style={{ color: "#fff", margin: 0 }}>
                    {selectedUser?.lab?.name ||
                      selectedUser?.uploaded_by?.full_name ||
                      "N/A"}
                  </Title>

                  <Title
                    level={4}
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      margin: "8px 0 0 0",
                    }}
                  >
                    {selectedUser?.lab?.location?.city ||
                      selectedUser?.uploaded_by?.email ||
                      "N/A"}
                  </Title>
                </Col>

                <Col span={8} style={{ textAlign: "right" }}>
                  <Avatar
                    size={64}
                    icon={<UserOutlined />}
                    src={
                      selectedUser?.report_file ||
                      selectedUser?.uploaded_by?.profile_img ||
                      notfound
                    }
                    style={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                    }}
                  />
                </Col>

                <Col style={{ width: "100%", textAlign: "end" }}>
                  <Button
                    onClick={() =>
                      handleDownloadReport(selectedUser?.report_file)
                    }
                  >
                    Download Report
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* CONDITIONAL BODY */}
            <Row gutter={[24, 24]}>
              {/* LEFT CARD */}
              <Col span={12}>
                <Card
                  title={
                    <span style={{ color: "#000" }}>
                      {selectedUser?.lab ? "Lab Contact Person" : "Uploaded By"}
                    </span>
                  }
                  size="small"
                  style={{
                    background: "#1e1e2f",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "10px",
                  }}
                >
                  {/* LAB REPORT */}
                  {selectedUser?.lab ? (
                    <>
                      <InfoRow
                        label="User ID"
                        value={
                          selectedUser?.lab?.contact_person?.id ||
                          selectedUser?.id
                        }
                      />
                      <InfoRow
                        label="Lab Assistant Name"
                        value={selectedUser?.lab?.contact_person?.full_name}
                      />
                      <InfoRow
                        label="Lab Assistant Number"
                        value={
                          selectedUser?.lab?.contact_person
                            ? `${selectedUser.lab.contact_person.country_code || ""} ${selectedUser.lab.contact_person.mobile || ""}`.trim()
                            : "N/A"
                        }
                      />
                      <InfoRow
                        label="Assistant Status"
                        badge={selectedUser?.lab?.contact_person?.status}
                      />
                    </>
                  ) : (
                    /* USER UPLOADED REPORT */
                    <>
                      <InfoRow
                        label="User Name"
                        value={selectedUser?.uploaded_by?.full_name}
                      />
                      <InfoRow
                        label="Email"
                        value={selectedUser?.uploaded_by?.email}
                      />
                      <InfoRow
                        label="Mobile"
                        value={`${selectedUser?.uploaded_by?.country_code || ""} ${selectedUser?.uploaded_by?.mobile || ""}`.trim()}
                      />
                      <InfoRow
                        label="User Status"
                        badge={selectedUser?.uploaded_by?.status}
                      />
                    </>
                  )}
                </Card>
              </Col>

              {/* RIGHT CARD (COMMON) */}
              <Col span={12}>
                <Card
                  title={<span style={{ color: "#000" }}>Report Details</span>}
                  size="small"
                  style={{
                    background: "#1e1e2f",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "10px",
                  }}
                >
                  <InfoRow
                    label="Upload Date"
                    value={moment(selectedUser.created_at).format("YYYY-MM-DD")}
                  />
                  <InfoRow
                    label="Uploaded By"
                    value={selectedUser?.uploaded_by?.full_name}
                  />
                  <InfoRow label="Notes" value={selectedUser?.notes} />
                </Card>
              </Col>
            </Row>
            {/* META + VISITS GRID */}
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
              {/* CARD 1: Medical Details */}
              {selectedUser?.meta && (
                <Col span={12}>
                  <Card
                    title={
                      <span style={{ color: "#000000ff" }}>
                        Medical Details
                      </span>
                    }
                    size="small"
                    style={cardStyle}
                  >
                    <MetaRow
                      label="Patient"
                      value={selectedUser.meta.patient?.full_name}
                    />
                    <MetaRow
                      label="Doctor Name"
                      value={selectedUser.meta.doctor_name}
                    />
                    <MetaRow
                      label="Qualification"
                      value={selectedUser.meta.doctor_qualification}
                    />
                    <MetaRow
                      label="Test Date"
                      value={selectedUser.meta.test_date}
                    />
                    <MetaRow
                      label="Report Date"
                      value={selectedUser.meta.report_date}
                    />
                  </Card>
                </Col>
              )}

              {/* CARD 2: Visit Summary */}
              {selectedUser?.meta && (
                <Col span={12}>
                  <Card
                    title={
                      <span style={{ color: "#000000ff" }}>Visit Summary</span>
                    }
                    size="small"
                    style={cardStyle}
                  >
                    <MetaRow
                      label="Last Visit"
                      value={selectedUser.meta.last_visit}
                    />
                    <MetaRow
                      label="Next Visit"
                      value={selectedUser.meta.next_visit}
                    />
                    <MetaRow
                      label="Total Visits"
                      value={selectedUser.visits?.length || 0}
                    />
                    <MetaRow
                      label="Remarks"
                      value={selectedUser.meta.remarks}
                    />
                  </Card>
                </Col>
              )}

              {/* CARD 3: Visits Timeline (Scrollable) */}
              {Array.isArray(selectedUser?.visits) &&
                selectedUser.visits.length > 0 && (
                  <Col span={12}>
                    <Card
                      title={
                        <span style={{ color: "#000000ff" }}>
                          Visits ({selectedUser.visits.length})
                        </span>
                      }
                      size="small"
                      style={{
                        ...cardStyle,
                        maxHeight: 260, // ✅ FIXED HEIGHT
                        overflow: "hidden",
                      }}
                      bodyStyle={{
                        padding: "12px",
                        overflowY: "auto", // ✅ SCROLL INSIDE
                        maxHeight: 200,
                      }}
                    >
                      {selectedUser.visits.map((visit, index) => (
                        <div
                          key={visit.id}
                          style={{
                            paddingBottom: 10,
                            marginBottom: 10,
                            borderBottom:
                              index !== selectedUser.visits.length - 1
                                ? "1px solid rgba(255,255,255,0.15)"
                                : "none",
                          }}
                        >
                          <Text strong style={{ color: "#bbb" }}>
                            {moment(visit.visit_date).format("DD MMM YYYY")}
                          </Text>

                          {visit.notes && (
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary">{visit.notes}</Text>
                            </div>
                          )}
                        </div>
                      ))}
                    </Card>
                  </Col>
                )}

              {/* CARD 4: Placeholder / Future Use (Keeps symmetry) */}
              {/* <Col span={12}>
                <Card
                  title={<span style={{ color: "#fff" }}>Additional Info</span>}
                  size="small"
                  style={cardStyle}
                >
                  <Text type="secondary">
                    Reserved for prescriptions, attachments, or actions.
                  </Text>
                </Card>
              </Col> */}
            </Row>
            {selectedUser?.lab && !selectedUser?.meta?.next_visit && (
              <div
                style={{
                  marginTop: 12,
                  padding: "6px 10px",
                  borderRadius: 6,
                  background: "#02a3e8",
                  color: "rgb(255 255 255)",
                  fontWeight: 500,
                  width: "fit-content",
                  height: "fit-content",
                }}
              >
                ✔ Treatment Completed
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Meta Modal */}
      <Modal
        title="Edit Treatment Info"
        open={editMetaVisible}
        onCancel={() => setEditMetaVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={editMetaForm}
          layout="vertical"
          onFinish={(values) => handleUpdateMeta(values)}
        >
          <Form.Item
            name="last_visit"
            label="Last Visit"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  const prevNextVisit = selectedUser?.meta?.next_visit;
                  if (!prevNextVisit) return Promise.resolve();

                  if (moment(value).isSameOrBefore(moment(prevNextVisit))) {
                    return Promise.reject(
                      new Error(
                        "Last visit must be greater than previous next visit date",
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item
            name="next_visit"
            label="Next Visit"
            tooltip="Leave empty if treatment is completed"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  const lastVisit = editMetaForm.getFieldValue("last_visit");

                  if (
                    lastVisit &&
                    moment(value).isSameOrBefore(moment(lastVisit))
                  ) {
                    return Promise.reject(
                      new Error("Next visit must be greater than last visit"),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="date"
              onChange={() => editMetaForm.validateFields(["last_visit"])}
            />
          </Form.Item>

          {/* ✅ Treatment Completed Indicator */}
          {!editMetaForm.getFieldValue("next_visit") && (
            <div
              style={{
                padding: "8px 12px",
                marginBottom: 12,
                borderRadius: 6,
                background: "#102a13",
                color: "#4caf50",
                fontWeight: 500,
              }}
            >
              ✔ Treatment Completed
            </div>
          )}

          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => setEditMetaVisible(false)}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={editMetaLoading}>
              Update
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Update Password Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserOutlined style={{ color: "#1890ff" }} />
            Update User Password
          </div>
        }
        open={changePasswordModal}
        onCancel={() => setChangePasswordModal(false)}
        footer={null}
        width={700}
        confirmLoading={createLoading || updateLoading}
      >
        <Form
          form={form2}
          layout="vertical"
          onFinish={(values) => handleUpdateUserPassword(values)}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label={lang("Password") || "Password"}
                rules={[
                  {
                    required: true,
                    message: "Please enter password",
                  },
                  {
                    min: 6,
                    message: "Password must be at least 6 characters",
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === "") {
                        return Promise.reject(
                          new Error("Password cannot be empty or whitespace"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.Password
                  // prefix={<LockOutlined />}
                  placeholder={lang("Enter_password") || "Enter password"}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirm_password"
                label={lang("Confirm_Password") || "Confirm Password"}
                dependencies={["password"]}
                rules={[
                  {
                    required: true,
                    message: "Please confirm password",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  // prefix={<LockOutlined />}
                  placeholder={lang("Confirm_password") || "Confirm password"}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => setChangePasswordModal(false)}
              disabled={createLoading || updateLoading}
            >
              {lang("Cancel") || "Cancel"}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={modalType === "add" ? createLoading : updateLoading}
              disabled={createLoading || updateLoading}
            >
              Update Password
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ReportManager;
